import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Upload, Trophy, Loader2, Camera, Clock, Award } from "lucide-react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { Overline } from "@/components/Cards";
import { HERO_MURAL } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const inputCls = "w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#00E5FF]";

function useCountdown(endsAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!endsAt) return null;
  const diff = new Date(endsAt).getTime() - now;
  if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, done: true };
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s, done: false };
}

function CountdownBox({ value, label }) {
  return (
    <div className="text-center">
      <div className="bg-black/60 border border-[#00E5FF]/30 px-3 py-2 min-w-[54px] font-display font-extrabold text-2xl text-[#00E5FF] tabular-nums">
        {String(value).padStart(2, "0")}
      </div>
      <div className="text-[10px] font-accent uppercase tracking-wider text-neutral-500 mt-1">{label}</div>
    </div>
  );
}

export default function Contest() {
  const [entries, setEntries] = useState([]);
  const [round, setRound] = useState(null);
  const [file, setFile] = useState(null);
  const [f, setF] = useState({ name: "", spot: "", caption: "" });
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const fileRef = useRef();
  const [liked, setLiked] = useState({});
  const cd = useCountdown(round?.ends_at);
  const open = !!round;

  const load = () => {
    api.get("/contest").then((r) => setEntries(r.data));
    api.get("/contest/rounds/current").then((r) => setRound(r.data.round)).finally(() => setLoaded(true));
  };
  useEffect(() => { load(); setLiked(JSON.parse(localStorage.getItem("contest_liked") || "{}")); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a photo or video first"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", f.caption);
    fd.append("spot", f.spot);
    fd.append("name", f.name);
    try {
      await api.post("/contest/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Entry submitted! Good luck 🎉");
      setFile(null); setF({ name: "", spot: "", caption: "" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const like = async (id) => {
    if (liked[id]) return;
    try {
      const { data } = await api.post(`/contest/${id}/like`);
      setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, likes: data.likes } : e)));
      const nl = { ...liked, [id]: true };
      setLiked(nl); localStorage.setItem("contest_liked", JSON.stringify(nl));
    } catch { /* ignore */ }
  };

  return (
    <div>
      <Seo title="RiNo Photo & Video Contest" description="Share your best RiNo photos and video shorts for a chance to win prizes. Snap the murals, the breweries, the vibe — and get featured on Visit RiNo." image={HERO_MURAL} />
      <PageHeader overline="Show Us Your RiNo" title="Photo & Video Contest" subtitle="Capture the district's murals, pours, and energy. Submit your best shots and short videos to win prizes and get featured across Visit RiNo." image={HERO_MURAL} />

      {/* Current round banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10">
        {!loaded ? (
          <div className="border border-white/10 bg-[#141414] h-32 animate-pulse" data-testid="contest-loading" />
        ) : open ? (
          <div className="border border-[#00E5FF]/30 bg-gradient-to-br from-[#04141a] to-[#141414] p-6 sm:p-8" data-testid="contest-round-banner">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 text-[#00E5FF] font-accent text-xs uppercase tracking-widest mb-2"><Trophy size={14} /> This Round · Open</div>
                <h2 className="font-display font-extrabold text-3xl text-white" data-testid="contest-round-title">{round.title}</h2>
                {round.theme && <p className="text-[#FFEA00] text-sm mt-1 font-accent">Theme: {round.theme}</p>}
                {round.prize && <p className="text-neutral-300 text-sm mt-2 flex items-center gap-2"><Award size={15} className="text-[#FF0055]" /> {round.prize}</p>}
              </div>
              {cd && !cd.done && (
                <div>
                  <div className="flex items-center gap-1.5 text-neutral-400 text-xs font-accent uppercase tracking-wider mb-2 justify-center lg:justify-end"><Clock size={13} /> Closes in</div>
                  <div className="flex gap-2" data-testid="contest-countdown">
                    <CountdownBox value={cd.d} label="Days" />
                    <CountdownBox value={cd.h} label="Hrs" />
                    <CountdownBox value={cd.m} label="Min" />
                    <CountdownBox value={cd.s} label="Sec" />
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="border border-white/10 bg-[#141414] p-8 text-center" data-testid="contest-closed-banner">
            <Clock size={26} className="text-neutral-500 mx-auto mb-3" />
            <h2 className="font-display font-extrabold text-2xl text-white">No open round right now</h2>
            <p className="text-neutral-400 text-sm mt-2">Submissions are paused between rounds. Check back soon — and see who took home the crown.</p>
          </div>
        )}
        <div className="mt-4 flex justify-center lg:justify-end">
          <Link to="/contest/winners" data-testid="winners-hall-link" className="inline-flex items-center gap-2 text-[#FFEA00] text-sm font-accent hover:underline"><Award size={15} /> Winners Hall</Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Submit */}
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-32 border border-[#FF0055]/40 bg-gradient-to-br from-[#1a0410] to-[#141414] p-6">
            <div className="flex items-center gap-2 text-[#FFEA00] font-accent text-sm mb-2"><Trophy size={16} /> {open ? "Enter This Round" : "Round Closed"}</div>
            <p className="text-neutral-400 text-sm leading-relaxed mb-5">
              {open
                ? (round.prize || "Monthly winners get RiNo gift cards, feature placement, and merch. Best video short wins a district experience for two.")
                : "Submissions reopen when the next round starts. Follow along in the Winners Hall."}
            </p>
            <form onSubmit={submit} className="space-y-3">
              <div onClick={() => open && fileRef.current?.click()} className={`border border-dashed p-6 text-center transition-colors ${open ? "border-white/20 cursor-pointer hover:border-[#00E5FF]" : "border-white/10 opacity-50 cursor-not-allowed"}`} data-testid="contest-dropzone">
                <Camera size={22} className="text-[#00E5FF] mx-auto mb-2" />
                <span className="text-neutral-400 text-sm">{file ? file.name : "Tap to add a photo or video"}</span>
                <input ref={fileRef} type="file" accept="image/*,video/*" disabled={!open} onChange={(e) => setFile(e.target.files[0])} data-testid="contest-file" className="hidden" />
              </div>
              <input placeholder="Your name" value={f.name} disabled={!open} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contest-name" className={inputCls} />
              <input placeholder="Which RiNo spot?" value={f.spot} disabled={!open} onChange={(e) => setF({ ...f, spot: e.target.value })} data-testid="contest-spot" className={inputCls} />
              <input placeholder="Caption" value={f.caption} disabled={!open} onChange={(e) => setF({ ...f, caption: e.target.value })} data-testid="contest-caption" className={inputCls} />
              <Button type="submit" disabled={busy || !open} data-testid="contest-submit" className="w-full bg-[#FF0055] hover:bg-[#ff2d74] rounded-none h-11 font-semibold disabled:opacity-50">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {open ? "Submit Entry" : "Submissions Closed"}
              </Button>
            </form>
          </div>
        </div>

        {/* Gallery */}
        <div className="lg:col-span-8">
          <Overline>The Gallery</Overline>
          <h2 className="font-display font-extrabold text-2xl text-white mt-2 mb-6">{entries.length} Entries{open ? " this round" : ""}</h2>
          {entries.length === 0 ? (
            <div className="text-neutral-500 py-16 text-center border border-white/10">{open ? "Be the first to submit a RiNo photo!" : "No entries in the current round yet."}</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {entries.map((e) => (
                <div key={e.id} className="border border-white/10 bg-[#141414] group relative" data-testid={`contest-entry-${e.id}`}>
                  {e.winner && <span className="absolute top-2 left-2 z-10 bg-[#FFEA00] text-black text-[10px] font-accent px-2 py-1 flex items-center gap-1"><Trophy size={10} /> Winner</span>}
                  <div className="relative h-48 overflow-hidden bg-black">
                    {e.media_type === "video"
                      ? <video src={`${API}/images/${e.image_id}/file`} className="w-full h-full object-cover" controls />
                      : <img src={`${API}/images/${e.image_id}/file`} alt={e.caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  </div>
                  <div className="p-3">
                    <div className="text-white text-sm truncate">{e.caption || e.spot || "RiNo"}</div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-neutral-500 text-xs truncate">by {e.name}</span>
                      <button onClick={() => like(e.id)} data-testid={`contest-like-${e.id}`}
                        className={`flex items-center gap-1 text-xs ${liked[e.id] ? "text-[#FF0055]" : "text-neutral-400 hover:text-[#FF0055]"}`}>
                        <Heart size={14} fill={liked[e.id] ? "#FF0055" : "none"} /> {e.likes}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
